import { useEffect, useMemo, useState } from "react";
import { CircleAlert, CircleCheck, Clock3, Link2, Loader2, Search } from "lucide-react";

const safeId = (value) => String(value || "unknown").replace(/[^a-zA-Z0-9_-]/g, "-");

const variantStatus = (variant) => {
  if ((variant.sync_drift_areas || []).length > 0) return { label: "Drift", Icon: CircleAlert, color: "text-red-400" };
  const status = variant.sync_status || (variant.last_synced_at ? "synced" : "pending");
  if (status === "failed") return { label: "Failed", Icon: CircleAlert, color: "text-red-400" };
  if (status === "syncing") return { label: "Syncing", Icon: Loader2, color: "text-cyan-400" };
  if (status === "synced") return { label: "Synced", Icon: CircleCheck, color: "text-emerald-400" };
  return { label: "Pending", Icon: Clock3, color: "text-amber-400" };
};

export const CampaignDomainTabs = ({ campaign, onSelectVariant, selectedVariant }) => {
  const variantsByDomain = useMemo(() => campaign.variants.reduce((groups, variant) => {
    groups[variant.domain] = [...(groups[variant.domain] || []), variant];
    return groups;
  }, {}), [campaign.variants]);

  return (
    <div className="border-b border-slate-800 bg-slate-950/25 px-4 py-4 sm:px-5">
      <div className="flex items-center justify-between gap-3" data-testid="detail-domain-tabs-header">
        <p className="text-xs font-semibold uppercase text-slate-400">Campaign domains</p>
        <span className="text-xs text-slate-500" data-testid="detail-variant-count">{campaign.variantCount} links</span>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Campaign domains">
        {campaign.domains.map((domain) => {
          const active = selectedVariant.domain === domain;
          return (
            <button key={domain} type="button" role="tab" aria-selected={active} onClick={() => onSelectVariant(variantsByDomain[domain][0].id)} className={`shrink-0 rounded-md border px-3 py-2 font-mono text-xs transition-colors ${active ? "border-cyan-500 bg-cyan-500/10 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600 hover:text-white"}`} data-testid={`domain-tab-${safeId(domain)}`}>
              {domain}<span className="ml-2 text-[10px] text-slate-500">{variantsByDomain[domain].length}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CampaignVariantList = ({ campaign, onSelectVariant, selectedVariant }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const domainVariants = useMemo(
    () => campaign.variants.filter((variant) => variant.domain === selectedVariant.domain),
    [campaign.variants, selectedVariant.domain],
  );
  const filteredVariants = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return domainVariants;
    return domainVariants.filter((variant) => [variant.path, variant.destination, variant.id]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [domainVariants, searchTerm]);

  useEffect(() => setSearchTerm(""), [campaign.id, selectedVariant.domain]);

  const rangeLabel = filteredVariants.length
    ? `1–${filteredVariants.length} of ${domainVariants.length}`
    : `0 of ${domainVariants.length}`;

  return (
    <aside className="min-w-0 border-b border-slate-800 bg-slate-950/25 xl:border-b-0 xl:border-r" data-testid="campaign-variant-navigator">
      <div className="border-b border-slate-800 px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <div><p className="text-xs font-semibold uppercase text-slate-300">Link variants</p><p className="mt-1 font-mono text-[11px] text-slate-500" data-testid="variant-list-range">{rangeLabel}</p></div>
          <Link2 className="h-4 w-4 text-cyan-400" />
        </div>
        <label className="relative mt-3 block">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
          <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search path or destination" className="h-9 w-full rounded-md border border-slate-700 bg-slate-950 pl-9 pr-3 font-mono text-xs text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-cyan-500/60" data-testid="variant-search-input" />
        </label>
      </div>
      <div className="max-h-[340px] overflow-y-auto xl:max-h-[650px]" data-testid="detail-path-variants" role="tablist" aria-label={`Links for ${selectedVariant.domain}`}>
        {filteredVariants.length === 0 ? (
          <div className="px-4 py-10 text-center" data-testid="variant-search-empty"><Search className="mx-auto h-6 w-6 text-slate-700" /><p className="mt-2 text-xs text-slate-500">No matching links</p></div>
        ) : filteredVariants.map((variant) => {
          const selected = variant.id === selectedVariant.id;
          const position = domainVariants.findIndex((item) => item.id === variant.id) + 1;
          const status = variantStatus(variant);
          const StatusIcon = status.Icon;
          const path = `/${String(variant.path || "").replace(/^\//, "")}`;
          return (
            <button key={variant.id} type="button" role="tab" aria-selected={selected} onClick={() => onSelectVariant(variant.id)} className={`group flex w-full min-w-0 items-center gap-3 border-b border-slate-800/80 px-3 py-3 text-left transition-colors ${selected ? "bg-cyan-500/10" : "hover:bg-slate-900/80"}`} data-testid={`variant-tab-${variant.id}`}>
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded border font-mono text-[10px] ${selected ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-slate-700 bg-slate-900 text-slate-500"}`}>{String(position).padStart(2, "0")}</span>
              <span className="min-w-0 flex-1"><span className={`block truncate font-mono text-xs ${selected ? "text-white" : "text-slate-300"}`} title={path} data-testid={`variant-path-${variant.id}`}>{path}</span><span className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500"><span className="inline-flex items-center gap-1" data-testid={`variant-sync-${variant.id}`}><StatusIcon className={`h-3 w-3 ${status.color} ${status.label === "Syncing" ? "animate-spin" : ""}`} />{status.label}</span><span>·</span><span data-testid={`variant-clicks-${variant.id}`}>{Number(variant.clicks || 0).toLocaleString()} clicks</span></span></span>
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${variant.is_active ? "bg-emerald-400" : "bg-slate-600"}`} title={variant.is_active ? "Active" : "Inactive"} data-testid={`variant-active-${variant.id}`} />
            </button>
          );
        })}
      </div>
    </aside>
  );
};